// frontend/src/pages/Admin/Submissions.jsx
import { useState, useEffect, useCallback } from 'react';
import {
    getAdminContactSubmissions,
    getAdminContactSubmissionStats,
    updateContactSubmissionStatus,
} from '../../api/services';

const STATUS_OPTIONS = [
    { value: '', label: 'All statuses' },
    { value: 'new', label: 'New' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'resolved', label: 'Resolved' },
];

const CATEGORY_OPTIONS = [
    { value: '', label: 'All categories' },
    { value: 'enterprise', label: 'Enterprise' },
    { value: 'support', label: 'Support' },
    { value: 'billing', label: 'Billing' },
    { value: 'feedback', label: 'Feedback' },
    { value: 'other', label: 'Other' },
];

// Status badge colour mapping. amber = needs attention, blue = being handled,
// green = closed out.
const STATUS_BADGE = {
    new: 'bg-amber-500/10 text-amber-400 border border-amber-500/20',
    in_progress: 'bg-blue-500/10 text-blue-400 border border-blue-500/20',
    resolved: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
};

const STATUS_LABEL = {
    new: 'New',
    in_progress: 'In progress',
    resolved: 'Resolved',
};

function StatusBadge({ status }) {
    return (
        <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-body font-medium ${STATUS_BADGE[status] || STATUS_BADGE.new}`}>
            {STATUS_LABEL[status] || status}
        </span>
    );
}

function SubmissionDetailModal({ submission, onClose, onStatusChange, updating }) {
    if (!submission) return null;

    // Reply via the admin's own email client (mailto). Also advances status to
    // in_progress — replying IS the first response.
    const handleReply = () => {
        const subject = encodeURIComponent(`Re: Your enquiry [${submission.ticket_code}]`);
        window.location.href = `mailto:${submission.email}?subject=${subject}`;
        if (submission.status === 'new') {
            onStatusChange(submission.id, 'in_progress');
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm">
            <div className="bg-[#0D1117] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-start justify-between p-6 border-b border-white/[0.06]">
                    <div>
                        <p className="font-mono font-bold text-brand-amber text-lg tracking-wide">{submission.ticket_code}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-xs text-brand-muted font-body capitalize">{submission.category}</span>
                            <span className="text-brand-muted">·</span>
                            <StatusBadge status={submission.status} />
                        </div>
                    </div>
                    <button onClick={onClose} className="text-white/40 hover:text-white transition-colors text-xl leading-none">✕</button>
                </div>

                {/* Details */}
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-[10px] font-body font-semibold uppercase tracking-widest text-brand-muted mb-1">Name</p>
                            <p className="text-sm text-brand-text font-body">{submission.name}</p>
                        </div>
                        <div>
                            <p className="text-[10px] font-body font-semibold uppercase tracking-widest text-brand-muted mb-1">Email</p>
                            <p className="text-sm text-brand-text font-body break-all">{submission.email}</p>
                        </div>
                        {submission.company && (
                            <div>
                                <p className="text-[10px] font-body font-semibold uppercase tracking-widest text-brand-muted mb-1">Company</p>
                                <p className="text-sm text-brand-text font-body">{submission.company}</p>
                            </div>
                        )}
                        <div>
                            <p className="text-[10px] font-body font-semibold uppercase tracking-widest text-brand-muted mb-1">Received</p>
                            <p className="text-sm text-brand-text font-body">{new Date(submission.created_at).toLocaleString()}</p>
                        </div>
                        {submission.responded_at && (
                            <div>
                                <p className="text-[10px] font-body font-semibold uppercase tracking-widest text-brand-muted mb-1">First response</p>
                                <p className="text-sm text-brand-text font-body">{new Date(submission.responded_at).toLocaleString()}</p>
                            </div>
                        )}
                    </div>

                    <div>
                        <p className="text-[10px] font-body font-semibold uppercase tracking-widest text-brand-muted mb-1.5">Message</p>
                        <div className="p-4 bg-white/[0.02] border border-white/[0.05] rounded-xl">
                            <p className="text-sm text-brand-text font-body whitespace-pre-wrap leading-relaxed">{submission.message}</p>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2 p-6 border-t border-white/[0.06]">
                    <button
                        onClick={handleReply}
                        disabled={updating}
                        className="px-4 py-2 bg-brand-amber hover:bg-amber-300 text-slate-950 rounded-xl text-xs font-body font-semibold transition-colors disabled:opacity-50"
                    >
                        Reply via email
                    </button>

                    {submission.status !== 'in_progress' && submission.status !== 'resolved' && (
                        <button
                            onClick={() => onStatusChange(submission.id, 'in_progress')}
                            disabled={updating}
                            className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] text-white rounded-xl text-xs font-body font-medium transition-colors disabled:opacity-50"
                        >
                            Mark In Progress
                        </button>
                    )}

                    {submission.status !== 'resolved' && (
                        <button
                            onClick={() => onStatusChange(submission.id, 'resolved')}
                            disabled={updating}
                            className="px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-400 rounded-xl text-xs font-body font-medium transition-colors disabled:opacity-50"
                        >
                            Mark Resolved
                        </button>
                    )}

                    {submission.status === 'resolved' && (
                        <button
                            onClick={() => onStatusChange(submission.id, 'in_progress')}
                            disabled={updating}
                            className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] text-white rounded-xl text-xs font-body font-medium transition-colors disabled:opacity-50"
                        >
                            Reopen
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function AdminSubmissions() {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [selected, setSelected] = useState(null);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [page, setPage] = useState(1);

    const [newCount, setNewCount] = useState(0);

    const PER_PAGE = 20;

    const fetchStats = useCallback(async () => {
        try {
            const { data } = await getAdminContactSubmissionStats();
            setNewCount(data?.by_status?.new || 0);
        } catch (err) {
            console.error('Failed to fetch submission stats:', err);
        }
    }, []);

    const fetchSubmissions = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, per_page: PER_PAGE };
            if (search) params.search = search;
            if (statusFilter) params.submission_status = statusFilter;
            if (categoryFilter) params.category = categoryFilter;

            const { data } = await getAdminContactSubmissions(params);
            setSubmissions(data.items || []);
        } catch (err) {
            console.error('Failed to fetch submissions:', err);
        } finally {
            setLoading(false);
        }
    }, [page, search, statusFilter, categoryFilter]);

    useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);
    useEffect(() => { fetchStats(); }, [fetchStats]);

    const handleStatusChange = async (id, newStatus) => {
        setUpdating(true);
        try {
            const { data } = await updateContactSubmissionStatus(id, newStatus);
            // Update the row in place + the open modal, and refresh the new-count.
            setSubmissions((prev) => prev.map((s) => (s.id === id ? data : s)));
            setSelected((prev) => (prev && prev.id === id ? data : prev));
            fetchStats();
        } catch (err) {
            console.error('Failed to update status:', err);
        } finally {
            setUpdating(false);
        }
    };

    return (
        <div>
            <h1 className="font-display text-2xl font-bold text-brand-text mb-2">Contact Submissions</h1>

            {/* New count badge */}
            <div className="mb-6">
                {newCount > 0 ? (
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-body font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                        {newCount} new {newCount === 1 ? 'submission' : 'submissions'} awaiting response
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-body font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        All caught up
                    </span>
                )}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
                <input
                    type="text"
                    placeholder="Search ticket, name, email…"
                    value={search}
                    onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                    className="input-field max-w-xs"
                />
                <select
                    value={statusFilter}
                    onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
                    className="input-field max-w-[180px]"
                >
                    {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select
                    value={categoryFilter}
                    onChange={(e) => { setPage(1); setCategoryFilter(e.target.value); }}
                    className="input-field max-w-[180px]"
                >
                    {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            </div>

            {/* Table */}
            <div className="card p-0 overflow-hidden">
                <table className="min-w-full">
                    <thead>
                        <tr className="border-b border-brand-border">
                            <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-widest font-body">Reference</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-widest font-body">Category</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-widest font-body">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-widest font-body">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-widest font-body">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-widest font-body">Received</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-10 text-center">
                                    <svg className="animate-spin h-6 w-6 text-brand-amber inline-block" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                                        <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                    </svg>
                                </td>
                            </tr>
                        ) : submissions.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-10 text-center text-brand-muted font-body text-sm">
                                    {search || statusFilter || categoryFilter ? 'No submissions match your filters.' : 'No submissions yet.'}
                                </td>
                            </tr>
                        ) : (
                            submissions.map((s) => (
                                <tr
                                    key={s.id}
                                    onClick={() => setSelected(s)}
                                    className="border-b border-brand-border last:border-0 hover:bg-brand-surface/50 transition-colors cursor-pointer"
                                >
                                    <td className="px-6 py-4 text-sm font-mono text-brand-amber">{s.ticket_code}</td>
                                    <td className="px-6 py-4 text-sm text-brand-muted font-body capitalize">{s.category}</td>
                                    <td className="px-6 py-4 text-sm text-brand-text font-body">{s.name}</td>
                                    <td className="px-6 py-4 text-sm text-brand-muted font-body">{s.email}</td>
                                    <td className="px-6 py-4"><StatusBadge status={s.status} /></td>
                                    <td className="px-6 py-4 text-sm text-brand-muted font-body">{new Date(s.created_at).toLocaleDateString()}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
                <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="btn-ghost text-sm py-2 px-4 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Previous
                </button>
                <span className="text-brand-muted font-body text-sm">Page {page}</span>
                <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={submissions.length < PER_PAGE}
                    className="btn-ghost text-sm py-2 px-4 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Next
                </button>
            </div>

            <SubmissionDetailModal
                submission={selected}
                onClose={() => setSelected(null)}
                onStatusChange={handleStatusChange}
                updating={updating}
            />
        </div>
    );
}